import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { theme, card, btnPrimary, btnSecondary, emptyFrame } from "../../theme";
import { frameworkLabel } from "../../nav/frameworkMeta";
import { controlRoute } from "../../utils/controlRoute";
import { clientOverviewPath, clientPortalPath } from "../../nav/consultantNav";
import { useConsultantShell } from "./ConsultantShellContext";
import { ScopeToggle } from "./ScopeToggle";
import { errText } from "./workspace";
import { FindingRiskLink, type LinkedRisk } from "../../components/FindingRiskLink";
import { controlRefWithStandard } from "../../lib/controlRef";

// ── Findings ──────────────────────────────────────────────────────────────
// Auditor findings across the portfolio, filtered and paged on the server
// (GET /consultant/findings). The status shown is the auditor's approval first,
// then the client's own progress; both parts are shown so "resolved" is never
// ambiguous. A consultant writes the client's progress only (PATCH, controls:write),
// and may escalate a finding to one of the client's risks (link-risk, controls:write).

type Shown = "open" | "acknowledged" | "resolved";

interface Finding {
  id: string;
  organizationId: string;
  organizationName: string | null;
  bureauName: string;
  controlId: string;
  frameworkId: string | null;
  text: string;
  status: Shown;
  auditorStatus: string;
  clientStatus: string | null;
  linkedRisk: LinkedRisk | null;
  createdAt: string;
}

interface FindingsResponse {
  total: number;
  page: number;
  pageSize: number;
  counts: Record<Shown | "all", number>;
  bureaux: Array<{ id: string; name: string }>;
  findings: Finding[];
}

const STATUS_META: Record<Shown, { label: string; color: string; bg: string }> = {
  open:         { label: "Open",         color: theme.color.dangerText,  bg: theme.color.dangerBg },
  acknowledged: { label: "Acknowledged", color: theme.color.warningText, bg: theme.color.warningBg },
  resolved:     { label: "Resolved",     color: theme.color.successText, bg: theme.color.successBg },
};

const NEXT: Record<Shown, Array<{ label: string; status: Shown }>> = {
  open:         [{ label: "Acknowledge", status: "acknowledged" }, { label: "Mark resolved", status: "resolved" }],
  acknowledged: [{ label: "Mark resolved", status: "resolved" }, { label: "Reopen", status: "open" }],
  resolved:     [{ label: "Reopen", status: "open" }],
};

const PAGE_SIZE = 50;

/** The client's own progress, in words. */
const CLIENT_PROGRESS: Record<string, string> = { acknowledged: "acknowledged", resolved: "says it is fixed" };

/** The auditor's verdict in the words the client sees (AuditorFeedback.tsx). */
const VERDICT: Record<string, string> = { open: "needs work", acknowledged: "observation", resolved: "approved" };

/** Where a finding's control lives in the client's own shell — the same page every other finding link opens. */
function controlHref(orgId: string, controlId: string, frameworkId: string | null): string {
  return `${clientPortalPath(orgId)}${controlRoute(controlId, frameworkId)}`;
}

export function FindingsPage() {
  const navigate = useNavigate();
  const { scope, portfolio, permissions } = useConsultantShell();
  const canWrite = permissions.includes("controls:write");
  const [status, setStatus] = useState<Shown | "all">("open");
  const [client, setClient] = useState("");
  const [bureau, setBureau] = useState("");
  const [framework, setFramework] = useState("");
  const [age, setAge] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<FindingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setError(null);
    api.get<FindingsResponse>("/consultant/findings", {
      scope,
      status: status === "all" ? undefined : status,
      organizationId: client || undefined,
      bureauId: bureau || undefined,
      frameworkId: framework || undefined,
      olderThanDays: age || undefined,
      q: q || undefined,
      page: String(page),
      pageSize: String(PAGE_SIZE),
    })
      .then((d) => { setData(d); setSelected(new Set()); })
      .catch((e) => setError(errText(e, "Could not load findings")));
  }, [scope, status, client, bureau, framework, age, q, page]);
  useEffect(load, [load]);

  const frameworks = useMemo(() => {
    const ids = new Set<string>();
    for (const o of portfolio.orgs) for (const f of o.frameworks) ids.add(f.frameworkId);
    return [...ids].sort((a, b) => frameworkLabel(a).localeCompare(frameworkLabel(b)));
  }, [portfolio.orgs]);

  async function change(ids: string[], next: Shown) {
    setBusy(ids.length > 1 ? "bulk" : ids[0]);
    const errors: Record<string, string> = {};
    for (const id of ids) {
      try { await api.patch(`/consultant/findings/${id}`, { status: next }); }
      catch (e) { errors[id] = errText(e, "Update failed"); }
    }
    setRowError(errors);
    setBusy(null);
    load();
  }

  const select: React.CSSProperties = { padding: "6px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5, maxWidth: 200 };
  const reset = () => setPage(1);
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const openOnPage = (data?.findings ?? []).filter((f) => f.status === "open");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(["open", "acknowledged", "resolved", "all"] as const).map((k) => {
            const active = status === k;
            const meta = k === "all" ? { label: "All", color: theme.color.accentText, bg: theme.color.accentBg } : STATUS_META[k];
            return (
              <button key={k} onClick={() => { setStatus(k); reset(); }}
                style={{ padding: "6px 12px", borderRadius: 3, fontSize: 12, cursor: "pointer", fontWeight: active ? 700 : 500, fontFamily: theme.font, border: `1px solid ${active ? meta.color : theme.color.border}`, background: active ? meta.bg : "transparent", color: active ? meta.color : theme.color.muted }}>
                {meta.label} <span style={{ marginLeft: 4, opacity: 0.75 }}>{data?.counts?.[k] ?? "–"}</span>
              </button>
            );
          })}
        </div>
        <ScopeToggle compact />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search text or control" aria-label="Search findings"
          style={{ ...select, minWidth: 200 }} />
        <select value={client} onChange={(e) => { setClient(e.target.value); reset(); }} aria-label="Client" style={select}>
          <option value="">Every client</option>
          {portfolio.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={bureau} onChange={(e) => { setBureau(e.target.value); reset(); }} aria-label="Certification body" style={select}>
          <option value="">Every body</option>
          {(data?.bureaux ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={framework} onChange={(e) => { setFramework(e.target.value); reset(); }} aria-label="Standard" style={select}>
          <option value="">Every standard</option>
          {frameworks.map((f) => <option key={f} value={f}>{frameworkLabel(f)}</option>)}
        </select>
        <select value={age} onChange={(e) => { setAge(e.target.value); reset(); }} aria-label="Age" style={select}>
          <option value="">Any age</option>
          <option value="30">Older than 30 days</option>
          <option value="90">Older than 90 days</option>
        </select>
        {canWrite && openOnPage.length > 1 && (
          <button onClick={() => change([...selected], "acknowledged")} disabled={selected.size === 0 || busy === "bulk"}
            style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12, marginLeft: "auto", opacity: selected.size === 0 ? 0.5 : 1 }}>
            {busy === "bulk" ? "Updating…" : `Acknowledge selected (${selected.size})`}
          </button>
        )}
      </div>

      {error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error} <button onClick={load} style={{ ...btnSecondary, padding: "3px 10px", fontSize: 12 }}>Retry</button></div>}
      {!data && !error && <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>}

      {data && data.findings.length === 0 && (
        <div style={{ ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: 40 }}>
          {status === "open" && !q && !client && !bureau && !framework && !age ? "No open findings — all clear." : "No findings match."}
        </div>
      )}

      {data && data.findings.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.findings.map((f) => {
            const meta = STATUS_META[f.status];
            return (
              <div key={f.id} style={{ ...card, padding: "14px 18px", display: "flex", gap: 12, borderLeft: `4px solid ${meta.color}` }}>
                {canWrite && f.status === "open" && openOnPage.length > 1 && (
                  <input type="checkbox" aria-label="Select finding" checked={selected.has(f.id)} style={{ marginTop: 3 }}
                    onChange={(e) => setSelected((s) => { const n = new Set(s); if (e.target.checked) n.add(f.id); else n.delete(f.id); return n; })} />
                )}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <button onClick={() => navigate(controlHref(f.organizationId, f.controlId, f.frameworkId))} title="Open the control in the client's portal"
                      style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 4, fontWeight: 700, background: theme.color.accentBg, color: theme.color.accentText, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                      {controlRefWithStandard(f.controlId) ?? f.controlId}
                    </button>
                    <div style={{ flex: 1, fontSize: 13, color: theme.color.text, lineHeight: 1.5 }}>{f.text}</div>
                    <span style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 4, fontWeight: 700, background: meta.bg, color: meta.color, whiteSpace: "nowrap" }}>{meta.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 11.5, color: theme.color.faint }}>
                    {f.organizationName && (
                      <button onClick={() => navigate(clientOverviewPath(f.organizationId))}
                        style={{ background: theme.color.accentBg, border: "none", borderRadius: 3, padding: "2px 8px", fontSize: 11, color: theme.color.accentText, cursor: "pointer", fontWeight: 600 }}>
                        {f.organizationName}
                      </button>
                    )}
                    <span>{f.bureauName}</span>
                    {f.frameworkId && <span>{frameworkLabel(f.frameworkId)}</span>}
                    <span>{new Date(f.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span title="The auditor's verdict and the client's own progress">
                      Auditor: {VERDICT[f.auditorStatus] ?? f.auditorStatus}
                      {/* Once the auditor has approved, the finding is closed: the client's own progress no longer applies. */}
                      {f.auditorStatus === "resolved" ? " · closed" : ` · Client: ${CLIENT_PROGRESS[f.clientStatus ?? ""] ?? "not started"}`}
                    </span>
                    {canWrite && (
                      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                        {/* A finding the auditor approved is theirs to reopen, never the consultant's. */}
                        {(f.auditorStatus === "resolved" ? [] : NEXT[f.status]).map(({ label, status: next }) => (
                          <button key={next} disabled={!!busy} onClick={() => change([f.id], next)}
                            style={{ ...btnSecondary, padding: "4px 10px", fontSize: 11, opacity: busy ? 0.6 : 1 }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {(f.linkedRisk || canWrite) && (
                    <FindingRiskLink
                      findingId={f.id}
                      orgId={f.organizationId}
                      linkedRisk={f.linkedRisk}
                      canEdit={canWrite}
                      onChange={(risk) => setData((d) => d && { ...d, findings: d.findings.map((x) => x.id === f.id ? { ...x, linkedRisk: risk } : x) })}
                      onOpenRisk={(id) => navigate(`${clientPortalPath(f.organizationId)}/risk-register?risk=${id}`)}
                    />
                  )}
                  {rowError[f.id] && <div style={{ fontSize: 11.5, color: theme.color.dangerText }}>{rowError[f.id]}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data && pages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", fontSize: 12.5, color: theme.color.subtle }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12, opacity: page <= 1 ? 0.5 : 1 }}>Previous</button>
          Page {page} of {pages} · {data.total} findings
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12, opacity: page >= pages ? 0.5 : 1 }}>Next</button>
        </div>
      )}
    </div>
  );
}
